import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { gqlClient } from '@/api/graphql-client';
import dashboardKeys from '@/api/queries/dashboard/keyFactory';
import { graphql } from '@/gql';

const GET_DASHBOARD_SUMMARY = graphql(`
  query GetDashboardSummary($input: DashboardSummaryInput) {
    dashboardSummary(input: $input) {
      revenueSecured {
        currentValue
        previousValue
        timeSeries {
          date
          value
        }
      }
      revenueReceived {
        currentValue
        previousValue
        timeSeries {
          date
          value
        }
      }
      outstanding {
        currentValue
        previousValue
        timeSeries {
          date
          value
        }
      }
      periodStart
      periodEnd
    }
  }
`);

export interface GetDashboardSummaryOptions {
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

/**
 * Calculate percentage change between two values.
 * Returns null if previous value is 0 (to avoid division by zero).
 */
function calculatePercentageChange(
  current: number,
  previous: number
): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

/**
 * Transform time series data into sparkline array (just values).
 */
function toSparkline(
  timeSeries: Array<{ date: string; value: number }>
): number[] {
  return timeSeries.map((point) => point.value);
}

const useGetDashboardSummary = (options?: GetDashboardSummaryOptions) => {
  const { dateRange } = options ?? {};

  const { data, ...rest } = useQuery({
    queryKey: dashboardKeys.summary(dateRange),
    queryFn: () =>
      gqlClient.request(GET_DASHBOARD_SUMMARY, {
        input: {
          from: dateRange?.from?.toISOString(),
          to: dateRange?.to?.toISOString(),
        },
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Transform the data to match component expectations
  const summary = useMemo(() => {
    if (!data?.dashboardSummary) return null;

    const { revenueSecured, revenueReceived, outstanding } =
      data.dashboardSummary;

    return {
      revenueSecured: {
        value: { value: revenueSecured.currentValue },
        previousValue: { value: revenueSecured.previousValue },
        percentageChange: calculatePercentageChange(
          revenueSecured.currentValue,
          revenueSecured.previousValue
        ),
        sparkline: toSparkline(revenueSecured.timeSeries),
      },
      revenueReceived: {
        value: { value: revenueReceived.currentValue },
        previousValue: { value: revenueReceived.previousValue },
        percentageChange: calculatePercentageChange(
          revenueReceived.currentValue,
          revenueReceived.previousValue
        ),
        sparkline: toSparkline(revenueReceived.timeSeries),
      },
      outstanding: {
        value: { value: outstanding.currentValue },
        sparkline: toSparkline(outstanding.timeSeries),
      },
    };
  }, [data]);

  return {
    data,
    summary,
    ...rest,
  };
};

export default useGetDashboardSummary;
